import { useCallback, useMemo } from 'react';
import { ToolbarSearch } from '../../../sharedComponents/filter/ToolbarSearch';
import {
    getServiceOrderCategory,
    getServiceOrderSearch,
    getServiceOrderSearchMode,
    ServiceOrderSearchMode,
    serviceOrdersSlice,
    setServiceOrderSearch,
} from '../../../reducers/serviceOrdersSlice';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { ServiceCategory } from '../commonServiceOrder.types';
import { useIntl } from 'react-intl';
import { MultiModeSearch, SearchModeOption } from '../../../sharedComponents/table/MultiModeSearch';

const DOCUMENT_ID_LENGTH = 35;

const ServiceOptionSearch = () => (
    <ToolbarSearch
        getSearchTerm={getServiceOrderSearch}
        setSearchTerm={setServiceOrderSearch}
        tooltipMessageId="outboundPortal.serviceOrders.table.toolbar.search.placeholder.tooltip"
        placeholderMessageId="outboundPortal.serviceOrders.table.toolbar.search.placeholder"
        maxLengthInput={DOCUMENT_ID_LENGTH}
    />
);

const ServiceOrderSearch = () => {
    const intl = useIntl();
    const searchMode = useAppSelector(getServiceOrderSearchMode);
    const searchTerm = useAppSelector(getServiceOrderSearch);
    const dispatch = useAppDispatch();

    const selectOptions: SearchModeOption<ServiceOrderSearchMode>[] = useMemo(
        () =>
            Object.values(ServiceOrderSearchMode).map((mode) => ({
                id: mode,
                label: intl.formatMessage({
                    id: `outboundOrderBook.serviceOrder.table.toolbar.search.mode.${mode}.label`,
                }),
                placeholder: intl.formatMessage({
                    id: `outboundOrderBook.serviceOrder.table.toolbar.search.mode.${mode}.placeholder`,
                }),
            })),
        [intl],
    );

    const onSearchModeChange = useCallback(
        (mode: ServiceOrderSearchMode) => dispatch(serviceOrdersSlice.actions.setSearchMode(mode)),
        [dispatch],
    );

    const onSearchTermChange = useCallback(
        (newValue: string) => dispatch(setServiceOrderSearch({ value: newValue })),
        [dispatch],
    );

    const onClear = useCallback(() => dispatch(setServiceOrderSearch({ value: '', immediate: true })), [dispatch]);

    const onEnterPressed = useCallback(
        () => dispatch(setServiceOrderSearch({ value: searchTerm, immediate: true })),
        [searchTerm, dispatch],
    );

    return (
        <MultiModeSearch
            modes={selectOptions}
            selectedMode={searchMode}
            onSearchModeChange={onSearchModeChange}
            searchTerm={searchTerm}
            onSearchTermChange={onSearchTermChange}
            onClear={onClear}
            onEnterPressed={onEnterPressed}
        />
    );
};

export const ServiceOrderToolbarSearch = () =>
    useAppSelector(getServiceOrderCategory) === ServiceCategory.OPTIONS ? (
        <ServiceOptionSearch />
    ) : (
        <ServiceOrderSearch />
    );
